import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { isBuilderPlan } from '@/services/auth/plan-slugs';
import { generatePath } from '@/services/router/generate-path';
import type { Route } from '@/services/router';
import type { LocalCommand } from './local-commands';
import type { PermissionGuard } from './use-navigation-search';
import { matchesLocalCommand } from './utils/search-commands';
import {
  addRoutingCommand,
  getNumericRouteParamFromPathname,
  matchesCurrentWorkspaceRoute,
  type Navigate,
} from './routing-command-utils';

type BuildSegmentRoutingCommandsArgs = {
  query: string;
  workspaceId: number;
  pathname: string;
  guard: PermissionGuard | null;
  navigate: Navigate;
};

type UseSegmentRoutingCommandsDeps = {
  workspaceId: number;
  guard: PermissionGuard | null;
  navigate: Navigate;
};

const SEGMENT_DETAIL_ROUTE = '/workspaces/:workspaceId/journeys/segments/:segmentId' satisfies Route;
const SEGMENT_STATIC_ROUTES = [
  '/workspaces/:workspaceId/journeys/segments',
  '/workspaces/:workspaceId/journeys/segments/new',
  '/workspaces/:workspaceId/journeys/segments/explore',
  '/workspaces/:workspaceId/journeys/segments/explore/people',
] as const satisfies readonly Route[];
const MAX_TYPED_SEGMENT_ROUTING_COMMANDS = 4;

export function getSegmentIdFromPathname(pathname: string, workspaceId: number): string | null {
  return getNumericRouteParamFromPathname({
    pathname,
    workspaceId,
    route: SEGMENT_DETAIL_ROUTE,
    paramName: 'segmentId',
  });
}

export function isSegmentPathname(pathname: string, workspaceId: number): boolean {
  if (workspaceId === 0) return false;
  if (getSegmentIdFromPathname(pathname, workspaceId) != null) return true;
  return SEGMENT_STATIC_ROUTES.some((route) => matchesCurrentWorkspaceRoute(pathname, route, workspaceId));
}

export function buildSegmentRoutingCommands({
  query,
  workspaceId,
  pathname,
  guard,
  navigate,
}: BuildSegmentRoutingCommandsArgs): LocalCommand[] {
  if (workspaceId === 0) return [];
  if (!isSegmentPathname(pathname, workspaceId)) return [];

  const trimmedQuery = query.trim();
  const isEmptyQuery = trimmedQuery.length === 0;

  const segmentId = getSegmentIdFromPathname(pathname, workspaceId);
  const canViewSegments = guard?.hasPermission('journeys/segments/view') === true;
  const canCreateSegments = guard?.hasPermission('journeys/segments/create') === true;
  const canEditSegments = guard?.hasPermission('journeys/segments/edit') === true;
  const isBuilder = isBuilderPlan(guard?.account);
  const commands: LocalCommand[] = [];

  if (segmentId != null && canViewSegments) {
    const segmentRouteParams = { workspaceId, segmentId };

    addRoutingCommand(
      commands,
      pathname,
      navigate,
      generatePath('/workspaces/:workspaceId/journeys/segments/:segmentId/overview', segmentRouteParams),
      {
        id: `segment-routing:${segmentId}:overview`,
        label: 'Open segment overview',
        description: 'Open the segment overview',
        icon: 'segment',
        keywords: ['segment', 'overview', 'details'],
      },
    );

    addRoutingCommand(
      commands,
      pathname,
      navigate,
      generatePath('/workspaces/:workspaceId/journeys/segments/:segmentId/people', segmentRouteParams),
      {
        id: `segment-routing:${segmentId}:people`,
        label: 'View people in segment',
        description: 'See people who match this segment',
        icon: 'segment',
        keywords: ['segment', 'people', 'members', 'audience'],
      },
    );

    addRoutingCommand(
      commands,
      pathname,
      navigate,
      generatePath('/workspaces/:workspaceId/journeys/segments/:segmentId/usage', segmentRouteParams),
      {
        id: `segment-routing:${segmentId}:usage`,
        label: 'View segment usage',
        description: 'See where this segment is used',
        icon: 'segment',
        keywords: ['segment', 'usage', 'used by', 'dependencies'],
      },
    );

    addRoutingCommand(
      commands,
      pathname,
      navigate,
      generatePath('/workspaces/:workspaceId/journeys/segments/:segmentId/audiences', segmentRouteParams),
      {
        id: `segment-routing:${segmentId}:audiences`,
        label: 'View ad audiences',
        description: 'View ad audiences connected to this segment',
        icon: 'segment',
        keywords: ['segment', 'audiences', 'ads', 'ad audiences'],
      },
    );
  }

  if (segmentId != null && canEditSegments) {
    addRoutingCommand(
      commands,
      pathname,
      navigate,
      generatePath('/workspaces/:workspaceId/journeys/segments/:segmentId/overview/edit', {
        workspaceId,
        segmentId,
      }),
      {
        id: `segment-routing:${segmentId}:edit`,
        label: 'Edit segment',
        description: "Change this segment's conditions",
        icon: 'edit',
        keywords: ['segment', 'edit', 'conditions'],
      },
    );
  }

  if (canCreateSegments) {
    if (!isBuilder) {
      addRoutingCommand(
        commands,
        pathname,
        navigate,
        generatePath(
          '/workspaces/:workspaceId/journeys/segments/explore',
          { workspaceId },
          { type: 'data-driven', source: 'segments' },
        ),
        {
          id: 'segment-routing:create-data-driven',
          label: 'Create data-driven segment',
          description: 'Automatically add or remove people based on conditions',
          icon: 'segment-data-driven',
          keywords: ['segment', 'data driven', 'dynamic', 'conditions', 'explorer'],
        },
      );
    }

    addRoutingCommand(
      commands,
      pathname,
      navigate,
      generatePath('/workspaces/:workspaceId/journeys/segments/new', { workspaceId }, { type: 'manual' }),
      {
        id: 'segment-routing:create-manual',
        label: 'Create manual segment',
        description: 'Add people via list uploads, workflows, or the API',
        icon: 'segment-manual',
        keywords: ['segment', 'manual', 'static', 'upload', 'api'],
      },
    );
  }

  if (canViewSegments) {
    addRoutingCommand(
      commands,
      pathname,
      navigate,
      generatePath('/workspaces/:workspaceId/journeys/segments', { workspaceId }),
      {
        id: 'segment-routing:index',
        label: 'Go to segments',
        description: 'Browse all segments',
        icon: 'segment',
        keywords: ['segment', 'segments', 'list'],
      },
    );
  }

  const matchingCommands = commands.filter(matchesLocalCommand(query));

  if (isEmptyQuery) return matchingCommands;

  return matchingCommands.slice(0, MAX_TYPED_SEGMENT_ROUTING_COMMANDS);
}

export function useSegmentRoutingCommands(query: string, deps: UseSegmentRoutingCommandsDeps): LocalCommand[] {
  const { pathname } = useLocation();
  const { workspaceId, guard, navigate } = deps;

  return useMemo(
    () => buildSegmentRoutingCommands({ query, workspaceId, pathname, guard, navigate }),
    [query, workspaceId, pathname, guard, navigate],
  );
}
